import { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { saveCart as saveCartApi } from '../api/orders';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const uidRef = useRef(null);
  // Marca la carga inicial del carrito desde localStorage tras iniciar sesión, para
  // NO escribir en Firestore en ese momento (no reiniciar el reloj de abandono).
  const hydratingRef = useRef(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      uidRef.current = user?.uid || null;
      if (user?.uid) {
        try {
          const saved = localStorage.getItem(`mi-mecanico-cart-${user.uid}`);
          hydratingRef.current = true;
          setItems(saved ? JSON.parse(saved) : []);
        } catch {
          hydratingRef.current = true;
          setItems([]);
        }
      } else {
        setItems([]);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (uidRef.current) {
      localStorage.setItem(`mi-mecanico-cart-${uidRef.current}`, JSON.stringify(items));
      // Persistencia en el servidor SOLO para usuarios autenticados: permite detectar
      // carritos abandonados desde el servidor (cron checkAbandonedCarts). No se
      // escribe en la hidratación inicial. reminderSentAt se limpia en cada cambio
      // real del carrito → un carrito re-modificado vuelve a ser elegible para aviso.
      //
      // ⚠️ QUÉ PASA CON ESA COPIA EL DÍA QUE `orders` SE ENCIENDA, comprobado en
      // el fuente del cron y no supuesto: `checkAbandonedCarts`
      // (`functions/src/cart.js`) SÍ consulta el interruptor y **se omite entera**
      // cuando `orders` se lee de Postgres. Lo hace a propósito y está razonado
      // allí: el correo se apoya en «¿compró después de dejar esto aquí?», y sin
      // ver los pedidos la respuesta sería siempre «no compró», así que el aviso
      // le llegaría justo a quien acaba de comprar.
      //
      // O sea: el recordatorio se apaga, pero se apaga CON AVISO en el registro y
      // por decisión escrita, no en silencio. La copia que se guarda aquí no
      // sirve a nadie hasta que alguien construya ese barrido sobre el API. Se
      // guarda igual para que el dato esté cuando eso ocurra: un carrito que no
      // se copió durante meses no se puede recuperar después.
      if (hydratingRef.current) {
        hydratingRef.current = false;
      } else {
        const uid = uidRef.current;
        // JSON round-trip: elimina valores `undefined` que Firestore rechaza.
        const safeItems = JSON.parse(JSON.stringify(items));
        // `carts` es del dominio `orders`. El interruptor se consulta AQUÍ dentro
        // y no al montar el proveedor: el carrito se guarda tras iniciar sesión,
        // y `config/migration` exige sesión para leerse. Consultarlo al montar
        // devolvería SIEMPRE 'firestore' (y no como error, sino como respuesta),
        // así que todo carrito se copiaría a la base muerta.
        //
        // 🔴 NINGUNA de las dos ramas propaga su fallo, y es deliberado: lo que
        // el cliente ve es `localStorage`, que ya quedó guardado dos líneas
        // arriba. Esta copia existe solo para que el recordatorio de carrito
        // abandonado pueda verla desde el servidor; tumbar la pantalla por no
        // poder copiarla sería cambiar una compra por un dato de marketing.
        snapshotMigrationConfig()
          .then((snap) => {
            if (snap.readsFrom('orders') === 'postgres') return saveCartApi(safeItems);
            return setDoc(doc(db, 'carts', uid), {
              userId: uid,
              items: safeItems,
              updatedAt: serverTimestamp(),
              reminderSentAt: null,
            }, { merge: true });
          })
          .catch(() => {});
      }
    }
  }, [items]);

  // El valor del contexto se recrea SOLO cuando cambia `items`: todo lo de abajo
  // depende únicamente de `items` (y de `setItems`, que es estable). Sin esto cada
  // render del proveedor entregaba un objeto nuevo y re-pintaba a todos los consumidores.
  const value = useMemo(() => {
  // Agrega producto al carrito — permite múltiples talleres
  const addToCart = (item) => {
    const cartKey = item.variationId ? `${item.productId}_${item.variationId}` : item.productId;
    setItems(prev => {
      const existing = prev.find(i => i.cartKey === cartKey);
      if (existing) {
        return prev.map(i =>
          i.cartKey === cartKey
            ? { ...i, quantity: i.quantity + (item.quantity || 1) }
            : i
        );
      }
      return [...prev, { ...item, cartKey, quantity: item.quantity || 1 }];
    });
    return true;
  };

  const removeFromCart = (cartKey) => {
    setItems(prev => prev.filter(i => i.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey, qty) => {
    if (qty < 1) return;
    setItems(prev => prev.map(i =>
      i.cartKey === cartKey ? { ...i, quantity: qty } : i
    ));
  };

  const toggleInstallation = (cartKey) => {
    setItems(prev => prev.map(i =>
      i.cartKey === cartKey
        ? { ...i, installationRequested: !i.installationRequested }
        : i
    ));
  };

  const clearCart = () => setItems([]);

  // Elimina solo los items de un taller específico (tras checkout parcial)
  const clearWorkshop = (workshopId) => {
    setItems(prev => prev.filter(i => i.workshopId !== workshopId));
  };

  // Retorna lista de talleres únicos que hay en el carrito
  const getWorkshops = () => {
    const map = {};
    items.forEach(i => {
      if (!map[i.workshopId]) {
        map[i.workshopId] = { workshopId: i.workshopId, workshopName: i.workshopName };
      }
    });
    return Object.values(map);
  };

  const getItemsByWorkshop = (workshopId) =>
    items.filter(i => i.workshopId === workshopId);

  const getSubtotalByWorkshop = (workshopId) =>
    getItemsByWorkshop(workshopId).reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);

  const getInstallationTotalByWorkshop = (workshopId) =>
    getItemsByWorkshop(workshopId).reduce((sum, i) =>
      i.installationRequested && i.installationPrice
        ? sum + i.installationPrice * i.quantity
        : sum
    , 0);

  const getItemCountByWorkshop = (workshopId) =>
    getItemsByWorkshop(workshopId).reduce((sum, i) => sum + i.quantity, 0);

  // Totales globales (todos los talleres)
  const getItemCount = () => items.reduce((sum, i) => sum + i.quantity, 0);
  const getSubtotal = () => items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
  const getInstallationTotal = () =>
    items.reduce((sum, i) =>
      i.installationRequested && i.installationPrice
        ? sum + i.installationPrice * i.quantity
        : sum
    , 0);
  const getTotal = () => getSubtotal() + getInstallationTotal();

  // Compat: primer taller del carrito (usado por Checkout cuando recibe workshopId por state)
  const workshopId = items.length > 0 ? items[0].workshopId : null;
  const workshopName = items.length > 0 ? items[0].workshopName : null;

  return {
      items,
      workshopId,
      workshopName,
      addToCart,
      removeFromCart,
      updateQuantity,
      toggleInstallation,
      clearCart,
      clearWorkshop,
      getWorkshops,
      getItemsByWorkshop,
      getSubtotalByWorkshop,
      getInstallationTotalByWorkshop,
      getItemCountByWorkshop,
      getItemCount,
      getSubtotal,
      getInstallationTotal,
      getTotal,
  };
  }, [items]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
